/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { useState } from 'react'
import type { ReactNode } from 'react'
import {
  ApartmentOutlined,
  DownOutlined,
  EditOutlined,
  LockOutlined,
  PlusOutlined,
  TeamOutlined,
} from '@ant-design/icons'
import { Tooltip } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import { t } from '@sportninja/common/i18n'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import {
  isScheduleNode,
  isTeamNode,
} from '@sportninja/common/features/competition/operations/utils/nodeTypes'
import {
  CHILD_COUNT_LABEL_KEY,
  childCountKind,
} from '@sportninja/common/features/competition/operations/utils/childCountKind'
import { panelGamesCount } from '@sportninja/common/features/competition/operations/utils/gamesCount'
import { divisionCountForNode } from '@sportninja/common/features/competition/operations/utils/divisionCount'
import { teamCountForNode } from '@sportninja/common/features/competition/operations/utils/teamCount'
import {
  formatRosterLockDate,
  isRosterLockPassed,
} from '@sportninja/common/features/competition/operations/utils/rosterLock'
import type {
  OperationsNode,
  OperationsScheduleNode,
} from '@sportninja/common/features/competition/operations/types/operations.types'
import { font, paragraphXSmall, weightSemiBold } from 'src/components/css'
import { STICKY_SCROLL_BOTTOM_VAR } from 'src/components/ts-components/DataGrid'
import EntityActionButton from 'src/components/ts-components/EntityActionButton'
import { SkeletonText } from 'src/components/ts-components/SkeletonBlock'
import StatusChip from 'src/components/ts-components/StatusChip'
import { canAddChild } from '../../utils/canAddChild'
import { nodeTypePalette } from '../../utils/nodeTypePalette'
import type { OperationsDnd } from '../../hooks/useOperationsDnd'
import DivisionTeamsGrid from '../division/DivisionTeamsGrid'
import SeasonDivisionsGrid from '../season/SeasonDivisionsGrid'
import type {
  DivisionRowAction,
  SeasonDivisionRow,
} from '../../grid/useSeasonDivisionsColumns'
import type {
  DivisionTeamRow,
  RowAction,
} from '../../grid/useDivisionTeamsColumns'
import type { RosterCounts, RosterTab } from '../roster/RosterSection'
import GridEmptyState from '../GridEmptyState'
import DivisionStandings from '../division/DivisionStandings'
import { useDivisionStandings } from '../../hooks/useDivisionStandings'
import EntityDetailsTab, { type DetailField } from './EntityDetailsTab'
import PanelTabs, { type PanelTab } from './PanelTabs'
import PanelHeader, { type PanelOrganization } from './PanelHeader'
import MoveTeamSelect from './MoveTeamSelect'
import RosterTransferActions from './RosterTransferActions'
import OrgTeamsTransferActions, {
  type OrgTeamsTransferActionsProps,
} from './OrgTeamsTransferActions'
import type { PanelStat } from './PanelStatStrip'
import { resolveAddButtonState } from './resolveAddButtonState'

const translate = t as (key: string, options?: unknown) => string

/**
 * The "More Details" disclosure is hidden for now. Kept behind a flag rather
 * than deleted — the field grid, its copy and its i18n keys all still work, so
 * bringing it back is a one-line change.
 */
const SHOW_MORE_DETAILS = false

/**
 * The division panel's tab strip is hidden for now: the teams sheet carries the
 * in-division record and PTS columns itself, so the separate Division
 * Performance chart is no longer the only way to see them.
 *
 * Kept behind a flag rather than deleted, matching SHOW_MORE_DETAILS above —
 * `PanelTabs`, `DivisionStandings` and the standings query all still work, so
 * restoring the chart is a one-line change.
 */
const SHOW_DIVISION_TABS = false

/** Tabs on a division's detail panel. */
type DivisionTab = 'teams' | 'standings'

const divisionTabs = (teamCount: number): PanelTab<DivisionTab>[] => [
  {
    key: 'teams',
    label: translate('common:team_plural'),
    count: teamCount,
  },
  // Not translated: no i18n key for this yet, and adding one to the shared
  // lang files was declined. `common:standings` is the wrong string — it is
  // reused by the divisions grid column and the main nav tab.
  { key: 'standings', label: 'Division Performance' },
]

const editLabelKey = (node: OperationsNode): string => {
  switch (node.type) {
    case 'team':
      return 'Web:editTeam'
    case 'division':
      return 'Web:editDivision'
    case 'conference':
      return 'Web:editConference'
    case 'season':
    default:
      return 'Web:editSeason'
  }
}

interface DetailPanelProps {
  node: OperationsNode
  ancestors: OperationsNode[]
  /** Owning organization, for the breadcrumb's leading crumb and the ID chips. */
  organization: PanelOrganization | null
  rootScheduleId: string
  /** The FULL tree, for the header's move-to-division options. */
  tree?: OperationsNode[]
  canEdit: boolean
  /** Team-level gate for Add Player, from the roster's own permissions. */
  canAddPlayer: boolean
  /** Team-level gate for Add Team Official, from the roster's own permissions. */
  canAddOfficial: boolean
  /**
   * True while the roster capabilities above are unresolved. Renders a
   * skeleton in the Add button's place instead of nothing, so the button
   * does not pop in and shift the header once the roster loads.
   */
  isRosterLoading?: boolean
  /** Team-level gate for Import Roster, from the roster's own permissions. */
  canImportRoster?: boolean
  /** Tooltip text when Import is denied; null when allowed. */
  importDisabledReason?: string | null
  /** Team-level gate for Export Roster. */
  canExportRoster?: boolean
  /** The selected roster; both CSV endpoints are scoped to it. */
  rosterId?: string | null
  /** Refetches the roster after a successful CSV import. */
  onRosterImported?: () => void
  onSelectNode: (nodeId: string) => void
  onEdit: (node: OperationsNode) => void
  onAddChild: (node: OperationsScheduleNode, kind: 'division' | 'team') => void
  onTeamRowAction: (action: RowAction, row: DivisionTeamRow) => void
  onDivisionRowAction: (
    action: DivisionRowAction,
    row: SeasonDivisionRow
  ) => void
  /** Roster surface for a selected team, injected by the tab. */
  rosterSlot: ReactNode
  /** Team nodes only: opens the add-player flow from the header. */
  onAddPlayer?: () => void
  /** Team nodes only: roster counts for the stat strip; null until loaded. */
  rosterCounts?: RosterCounts | null
  /** Visible roster tab — decides whether Add reads Player or Team Official. */
  rosterTab?: RosterTab
  /** Suppresses Add Team while registration is open — see `canAddChild`. */
  isRegistrationOn?: boolean
  /** Drag controller shared with the tree rail — see `useOperationsDnd`. */
  dnd: OperationsDnd
  /**
   * The organization's team and roster import/export, shown on the root
   * competition only. Null when the viewer lacks the org's `update`.
   */
  orgTransfer?: OrgTeamsTransferActionsProps | null
}

const unset = (value: string | null | undefined): DetailField['value'] =>
  value ?? translate('common:notSet')

const scheduleFields = (node: OperationsScheduleNode): DetailField[] => [
  { label: translate('common:name'), value: node.name },
  { label: translate('common:type'), value: node.type },
  {
    label: translate('common:order'),
    value: unset(node.sortOrder === null ? null : String(node.sortOrder)),
    isUnset: node.sortOrder === null,
  },
  {
    label: translate('common:rosterLock'),
    value: unset(node.lockRosterDate),
    isUnset: !node.lockRosterDate,
  },
  {
    label: translate('common:teamCost'),
    value: unset(node.teamCost === null ? null : String(node.teamCost)),
    isUnset: node.teamCost === null,
  },
  {
    label: translate('common:deposit'),
    value: unset(node.deposit === null ? null : String(node.deposit)),
    isUnset: node.deposit === null,
  },
  {
    label: translate('common:standings'),
    value: node.isVisibleStandings
      ? translate('Web:visible')
      : translate('Web:hidden'),
  },
  {
    label: translate('Web:registration'),
    value: node.isRegistrationOn
      ? translate('common:open')
      : translate('common:closed'),
  },
]

/**
 * The right-hand panel. One shell for every node type; the body switches on
 * what is selected — divisions grid for a season or conference, teams grid for
 * a division, the roster for a team.
 */
const DetailPanel = ({
  node,
  ancestors,
  organization,
  rootScheduleId,
  tree = [],
  canEdit,
  canAddPlayer,
  canAddOfficial,
  isRosterLoading = false,
  canImportRoster = false,
  importDisabledReason = null,
  canExportRoster = false,
  rosterId = null,
  onRosterImported,
  onSelectNode,
  onEdit,
  onAddChild,
  onTeamRowAction,
  onDivisionRowAction,
  rosterSlot,
  onAddPlayer,
  rosterCounts,
  rosterTab = 'players',
  isRegistrationOn = false,
  dnd,
  orgTransfer = null,
}: DetailPanelProps) => {
  'use memo'

  // Open by default — the field grid is the point of the panel, not an extra.
  const [showDetails, setShowDetails] = useState(true)

  // Teams stays the default: Operations is primarily a structure-editing tool.
  const [divisionTab, setDivisionTab] = useState<DivisionTab>('teams')

  // What this node WOULD contain — names the empty state, since there are no
  // children to infer it from.
  const emptyKind = childCountKind(node) === 'team' ? 'team' : 'division'

  const children: OperationsNode[] = isTeamNode(node) ? [] : node.children
  const teams = children.filter(isTeamNode)
  const childSchedules = children.filter(isScheduleNode)

  // Standings exist for any schedule node that holds teams directly AND
  // publishes them — a conference with teams under it earns the record and PTS
  // columns exactly as a division does. `/schedules/:id/standings` is
  // schedule-scoped rather than division-scoped, `is_visible_standings` is
  // mapped for every schedule node by `buildOperationsTree`, and the grid joins
  // rows onto teams by `teamId`, so none of the pieces care about the type.
  // Seasons are excluded by `teams.length` instead: they hold conferences or
  // divisions, never teams, so the teams grid they'd feed never renders.
  const hasStandings =
    isScheduleNode(node) && node.isVisibleStandings === true && teams.length > 0

  // Fetched whenever the division HAS standings, not when a tab is open: the
  // teams sheet's record and PTS columns need this data and are now always on
  // screen. Still nothing at all for a division that publishes no standings,
  // or for a team node.
  // The API scopes its standings response by `groupBy`, so it has to name the
  // level of the id being sent — a conference id grouped by Division comes back
  // for the wrong grouping.
  const isConference = isScheduleNode(node) && node.type === 'conference'

  const standings = useDivisionStandings({
    scheduleId: isTeamNode(node) ? null : node.scheduleId,
    enabled: hasStandings,
    groupBy: isConference ? 'Conference' : 'Division',
  })

  const stats: PanelStat[] = isTeamNode(node)
    ? [
        // Roster counts lead, matching the design. They arrive from
        // RosterSection (see `rosterCounts`) and are omitted until the roster
        // loads rather than shown as a misleading 0.
        ...(rosterCounts
          ? [
              {
                label: translate('common:player_plural'),
                value: String(rosterCounts.players),
              },
              {
                label: translate('common:spare_plural'),
                value: String(rosterCounts.spares),
              },
              {
                label: translate('common:teamOfficial_plural'),
                value: String(rosterCounts.officials),
              },
            ]
          : []),
        {
          label: translate('common:game_plural', { count: 2 }),
          value: String(node.gamesCount),
        },
      ]
    : [
        {
          // See `childCountKind`: fixed by node type only where the hierarchy
          // fixes it, so an empty division never reads "0 divisions", a season
          // of conferences says "conferences", and a legacy conference holding
          // teams directly says "teams" rather than "divisions".
          label: translate(CHILD_COUNT_LABEL_KEY[childCountKind(node)]),
          value: String(children.length),
        },
        // A season of conferences counts the conferences above, so the
        // divisions inside them get their own figure.
        ...(childCountKind(node) === 'conference'
          ? [
              {
                label: translate('common:division_plural'),
                value: String(divisionCountForNode(node)),
              },
            ]
          : []),
        // A season or conference counts its divisions above, so its teams —
        // every one beneath it, through each level — get their own figure. A
        // node whose children ARE teams already shows that count.
        ...(childCountKind(node) === 'team'
          ? []
          : [
              {
                label: translate('common:team_plural'),
                value: String(teamCountForNode(node)),
              },
            ]),
        {
          // The top competition's own `games_count`; every level below sums
          // its team descendants (see `panelGamesCount`). Childless nodes
          // report 0, not unknown.
          label: translate('common:game_plural', { count: 2 }),
          value: String(panelGamesCount(node, rootScheduleId)),
        },
      ]

  const rosterLockLabel = isScheduleNode(node)
    ? formatRosterLockDate(node.lockRosterDate)
    : null

  const pills = (
    <>
      {/* `node.type` is a raw lowercase union ('team', 'division', …).
          Uppercased in CSS rather than JS so there is no string to mangle and
          nothing to translate — it is a type marker, not prose.

          Not a `StatusChip`: this is a TYPE marker, not a status, and it takes
          the same per-type palette as the tree rail's badges so the selected
          node is the same colour in both places. `StatusChip`'s tones are a
          fixed semantic set whose red is a lighter hue than the tree's. */}
      <span css={typePillStyle(node.type)}>{node.type}</span>
      {/* Only the ACTIVE state is badged. Registration being closed is the
          normal resting state for most of a competition's life, so a pill on
          every such node is noise — open registration is the notable case. */}
      {isScheduleNode(node) && node.isRegistrationOn && (
        <StatusChip tone='ok'>
          {/* Uppercased in CSS, not JS: the string is translated, and
              `toUpperCase()` mangles locales with special casing rules. */}
          <span css={pillCapsStyle}>
            {translate('common:registrationOpen')}
          </span>
        </StatusChip>
      )}
    </>
  )

  // Its own line under the crest, not another pill. The lock turns amber once
  // the date has passed — roster adds are blocked from then on.
  const rosterLock =
    isScheduleNode(node) && rosterLockLabel ? (
      <Tooltip
        title={capitalizeWords(translate('common:rosterLock'))}
        placement='top'
      >
        <span css={rosterLockStyle}>
          <LockOutlined
            css={rosterLockIconStyle(isRosterLockPassed(node.lockRosterDate))}
          />
          {capitalizeWords(translate('common:roster'))}
          <span css={rosterLockDateStyle}>{rosterLockLabel}</span>
        </span>
      </Tooltip>
    ) : null

  const addable = isScheduleNode(node)
    ? canAddChild(node, isRegistrationOn)
    : null

  const addButtonState = isTeamNode(node)
    ? resolveAddButtonState({
        rosterTab,
        isLoading: isRosterLoading,
        canAddPlayer,
        canAddOfficial,
      })
    : 'none'

  // Row 1: relocation and editing — what the entity IS.
  const actions = canEdit ? (
    <>
      {isTeamNode(node) && (
        <MoveTeamSelect
          team={node}
          tree={tree}
          rootScheduleId={rootScheduleId}
          onMoved={onSelectNode}
          isRegistrationOn={isRegistrationOn}
        />
      )}

      {/* Primary only on a team, where Edit is the row's main action. A
          season/conference/division row also carries Add Division or Add
          Team, which should be the emphasised one — so Edit stays outlined
          there rather than competing with it. */}
      <EntityActionButton
        label={translate(editLabelKey(node))}
        variant={isTeamNode(node) ? 'primary' : 'outlined'}
        icon={<EditOutlined />}
        onClick={() => onEdit(node)}
      />
      {addable?.division && isScheduleNode(node) && (
        <EntityActionButton
          label={translate('Web:addDivision')}
          variant='primary'
          icon={<PlusOutlined />}
          onClick={() => onAddChild(node, 'division')}
        />
      )}
      {addable?.team && isScheduleNode(node) && (
        <EntityActionButton
          label={translate('Web:addTeam')}
          variant='primary'
          icon={<PlusOutlined />}
          onClick={() => onAddChild(node, 'team')}
        />
      )}
    </>
  ) : null

  // Only the root competition carries the org-wide import/export: the files
  // cover every team in the organization, not one division's.
  const isRootNode = isScheduleNode(node) && node.scheduleId === rootScheduleId
  const orgTransferActions =
    isRootNode && orgTransfer ? (
      <OrgTeamsTransferActions {...orgTransfer} />
    ) : null

  // Row 2: what the entity CONTAINS — a team's roster operations, or the
  // organization's teams and rosters on the root competition. Other schedule
  // nodes render one row and the header stays compact.
  const secondaryActions =
    canEdit && isTeamNode(node) ? (
      <>
        {/* The row is right-aligned, so Import/Export come FIRST in the DOM to
            render to the LEFT of Add Player — which keeps the primary action
            on the outside edge, directly under Edit Team.

            Shown on both roster tabs, unlike the Team screen, which hides them
            on Officials. */}
        {onRosterImported && (
          <RosterTransferActions
            teamId={node.teamId}
            teamName={node.name}
            rosterId={rosterId}
            canImport={canImportRoster}
            canImportOfficials={canAddOfficial}
            importDisabledReason={importDisabledReason}
            canExport={canExportRoster}
            isLoading={isRosterLoading}
            onImported={onRosterImported}
          />
        )}

        {/* The modal it opens is mounted down in RosterSection, which is
          inside RosterProvider — see `addPlayerSignal`. Its label follows the
          visible roster tab, and RosterSection opens the matching modal off
          the same signal.

          Three states, not two: while the roster loads, `canAddPlayer`/
          `canAddOfficial` read false same as "denied" — a skeleton reserves
          the button's footprint so nothing shifts once the real answer
          lands. Once loaded, false means denied and nothing renders — a
          permanent skeleton would be worse than the pop this replaces. */}
        {isTeamNode(node) && onAddPlayer && addButtonState === 'skeleton' && (
          <span
            css={addButtonSkeletonStyle}
            data-testid='add-button-skeleton'
            aria-hidden
          >
            <SkeletonText width={16} height={16} />
            <SkeletonText width={72} height={12} />
          </span>
        )}
        {isTeamNode(node) && onAddPlayer && addButtonState === 'button' && (
          <EntityActionButton
            label={translate(
              rosterTab === 'officials'
                ? 'Web:addTeamOfficial'
                : 'Web:addPlayer'
            )}
            variant='primary'
            icon={<PlusOutlined />}
            onClick={onAddPlayer}
          />
        )}
      </>
    ) : (
      orgTransferActions
    )

  return (
    <section css={panelStyle}>
      <PanelHeader
        node={node}
        ancestors={ancestors}
        organization={organization}
        stats={stats}
        pills={pills}
        meta={rosterLock}
        actions={actions}
        secondaryActions={secondaryActions}
        onSelectNode={onSelectNode}
        rootScheduleId={rootScheduleId}
      />

      <div css={bodyStyle}>
        {isTeamNode(node) ? (
          rosterSlot
        ) : (
          <>
            {SHOW_DIVISION_TABS && hasStandings && (
              <PanelTabs
                tabs={divisionTabs(teams.length)}
                activeKey={divisionTab}
                onChange={setDivisionTab}
              />
            )}

            {SHOW_DIVISION_TABS &&
            hasStandings &&
            divisionTab === 'standings' ? (
              <DivisionStandings
                rows={standings.rows}
                isLoading={standings.isLoading}
                isError={standings.isError}
                onRetry={standings.refetch}
              />
            ) : teams.length > 0 ? (
              <DivisionTeamsGrid
                divisionId={node.id}
                teams={teams}
                canEdit={canEdit}
                isRegistrationOn={isRegistrationOn}
                onSelectTeam={onSelectNode}
                onRowAction={onTeamRowAction}
                onDrop={dnd.handleDrop}
                localOrder={dnd.localOrder}
                dragPendingIds={dnd.pendingIds}
                standingsRows={standings.rows}
                showStandings={hasStandings}
                isStandingsLoading={standings.isLoading}
                isConference={isConference}
              />
            ) : childSchedules.length > 0 ? (
              <SeasonDivisionsGrid
                parent={node}
                divisions={childSchedules}
                rootScheduleId={rootScheduleId}
                canEdit={canEdit}
                onSelectDivision={onSelectNode}
                onRowAction={onDivisionRowAction}
              />
            ) : (
              <GridEmptyState
                icon={
                  emptyKind === 'team' ? (
                    <TeamOutlined />
                  ) : (
                    <ApartmentOutlined />
                  )
                }
                title={capitalizeWords(
                  translate('common:noEntityYet', {
                    entity: translate(`common:${emptyKind}_plural`),
                  })
                )}
                hint={translate('common:addEntityHint', {
                  entity: translate(`common:${emptyKind}`),
                })}
              />
            )}

            {SHOW_MORE_DETAILS && (
              <>
                <button
                  type='button'
                  css={detailsToggleStyle}
                  aria-expanded={showDetails}
                  onClick={() => setShowDetails((open) => !open)}
                >
                  {translate('common:moreDetails')}
                  <DownOutlined css={detailsChevronStyle(showDetails)} />
                </button>

                {showDetails && (
                  <EntityDetailsTab fields={scheduleFields(node)} />
                )}
              </>
            )}
          </>
        )}
      </div>
    </section>
  )
}

/* Inherits the header meta line's paragraphXSmall and muted colour; the date
   is the value, so it reads brighter than its label. */
const rosterLockStyle = css`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`

const rosterLockIconStyle = (isPassed: boolean) => css`
  color: ${isPassed ? colors.WARNING_300 : 'inherit'};
`

const rosterLockDateStyle = css`
  ${weightSemiBold}
  color: ${colors.NEUTRAL_0};
`

/* Same caps treatment as the type pill beside it. */
const pillCapsStyle = css`
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

/* Geometry copied from `StatusChip` so this sits identically beside the
   registration pill; only the palette differs — see the call site. */
const typePillStyle = (type: string) => {
  const palette = nodeTypePalette(type)

  return css`
    ${paragraphXSmall}
    ${weightSemiBold}
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 2px 8px;
    border-radius: 20px;
    white-space: nowrap;
    ${pillCapsStyle}
    color: ${palette.fg};
    background: ${palette.bg};

    &::before {
      content: '';
      width: 5px;
      height: 5px;
      border-radius: 50%;
      background: currentColor;
    }
  `
}

const panelStyle = css`
  display: flex;
  flex-direction: column;
  min-width: 0;
  /* Without min-height:0 a flex item refuses to shrink below its content, so
     the body's overflow:auto never engages and a long roster pushes the panel
     past the viewport instead of scrolling inside it. */
  min-height: 0;
  background: ${colors.SECONDARY_500};
`

/** Shared by the body's padding and the sticky-scrollbar offset that cancels it. */
const BODY_PADDING_BOTTOM = 20

const bodyStyle = css`
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 14px 20px ${BODY_PADDING_BOTTOM}px;
  /* The roster grids' sticky scrollbar sits flush on this scroller's bottom
     edge rather than ${BODY_PADDING_BOTTOM}px up, over the rows. */
  ${STICKY_SCROLL_BOTTOM_VAR}: -${BODY_PADDING_BOTTOM}px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`

/**
 * Reserves the same footprint as `EntityActionButton` (padding: 8px, gap:
 * 6px, 16px icon) so nothing shifts when this swaps for the real button.
 * Widths (16px icon slot, 72px label) are tuned to "Add Player"/"Add Team
 * Official" at the button's font size — matching, not exact, since a
 * skeleton doesn't need pixel-perfect text metrics to prevent layout jump.
 */
const addButtonSkeletonStyle = css`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px;
  border-radius: 8px;
`

const detailsToggleStyle = css`
  /* Header face (Rift / Neue Plak Condensed), matching the panel title — both
     tenants render it uppercase, so no capitalize helper is needed on top. */
  ${font.title}
  ${weightSemiBold}
  font-size: 15px;
  line-height: 120%;
  /* Set after the font token — Canlan's title face ships its own 2px, so this
     keeps both tenants on the same tracking. */
  letter-spacing: 1.5px;
  align-self: flex-start;
  display: flex;
  align-items: center;
  gap: 7px;
  border: none;
  background: none;
  color: ${colors.NEUTRAL_50};
  cursor: pointer;
  padding: 0;

  &:hover {
    color: ${colors.NEUTRAL_0};
  }
`

/** Points down when collapsed, up when open — the arrow shows what a click does. */
const detailsChevronStyle = (open: boolean) => css`
  font-size: 10px;
  transition: transform 150ms ease;
  transform: rotate(${open ? 180 : 0}deg);
`

export default DetailPanel
